import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaCheckCircle, FaRegStar, FaStar } from "react-icons/fa";
import { api } from "../../config/api";

const COUNTRY_FLAGS = {
  us: {
    label: "US",
    alt: "USA flag",
    url: "https://firebasestorage.googleapis.com/v0/b/coursepivot-a7190.firebasestorage.app/o/Coursepivot%20Main%20Site%2FUSA%20flag.png?alt=media&token=b5d8b8ab-96fb-4efd-b18a-fa429d9fc204",
  },
  australia: {
    label: "AU",
    alt: "Australia flag",
    url: "https://firebasestorage.googleapis.com/v0/b/coursepivot-a7190.firebasestorage.app/o/Coursepivot%20Main%20Site%2FAustralia%20flag.png?alt=media&token=659bf558-6b85-4383-a2fc-6c3275274f51",
  },
  uk: {
    label: "UK",
    alt: "United Kingdom flag",
    url: "https://firebasestorage.googleapis.com/v0/b/coursepivot-a7190.firebasestorage.app/o/Coursepivot%20Main%20Site%2FUnited-kingdom%20Flag.png?alt=media&token=8f638c04-edea-4da9-8678-b9652e510cdf",
  },
  canada: {
    label: "CA",
    alt: "Canada flag",
    url: "https://firebasestorage.googleapis.com/v0/b/coursepivot-a7190.firebasestorage.app/o/Coursepivot%20Main%20Site%2FCanada%20flag.png?alt=media&token=4bc43335-80a8-4070-a38c-8af113197981",
  },
};

const fallbackValue = (value) =>
  value === null || value === undefined || value === "" ? "Not provided" : value;

const getInitials = (firstName, lastName) => {
  const initials = `${firstName?.[0] || ""}${lastName?.[0] || ""}`.trim();
  return initials || "GP";
};

const getCountryFlag = (country) => {
  const countryValue = `${country?.name || country?.code || country || ""}`.toLowerCase();

  if (["us", "usa", "u.s.", "u.s.a.", "united states", "united states of america"].includes(countryValue)) {
    return COUNTRY_FLAGS.us;
  }

  if (["au", "aus", "australia"].includes(countryValue)) {
    return COUNTRY_FLAGS.australia;
  }

  if (["uk", "gb", "gbr", "britain", "great britain", "united kingdom"].includes(countryValue)) {
    return COUNTRY_FLAGS.uk;
  }

  if (["ca", "can", "canada"].includes(countryValue)) {
    return COUNTRY_FLAGS.canada;
  }

  return null;
};

const formatCompanyTenure = (createdAt) => {
  if (!createdAt) return "1 Year";

  const startDate = new Date(createdAt);

  if (Number.isNaN(startDate.getTime())) return "1 Year";

  const now = new Date();
  let years = now.getFullYear() - startDate.getFullYear();
  const anniversaryThisYear = new Date(startDate);
  anniversaryThisYear.setFullYear(startDate.getFullYear() + years);

  if (now > anniversaryThisYear) years += 1;

  years = Math.max(1, years);

  return `${years} ${years === 1 ? "Year" : "Years"}`;
};

const normalizeRatingSummary = (summary = []) => {
  const rating = summary?.[0] || {};

  return {
    completedOrdersCount: Number(rating.completedOrdersCount || 0),
    ratedOrdersCount: Number(rating.ratedOrdersCount || 0),
    overallRating: Number(rating.overallRating || 0),
  };
};

const normalizePerformanceSnapshot = (summary = []) => {
  const snapshot = summary?.[0] || {};
  const successRate =
    snapshot.successRate === null || snapshot.successRate === undefined
      ? null
      : Number(snapshot.successRate);

  return {
    successRate: Number.isFinite(successRate) ? successRate : null,
  };
};

const normalizeRecentFeedback = (feedback = []) =>
  feedback.map((item) => ({
    orderId: item.orderId || "Not provided",
    subject: item.subject || "Not provided",
    rating: Number(item.rating || 0),
    customerFeedback: item.customerFeedback || "",
    completedDate: item.completedDate || "",
  }));

const formatDate = (date) => {
  if (!date) return "Date unavailable";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return "Date unavailable";

  return parsedDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

function StarRating({ rating }) {
  const roundedRating = Math.round(Number(rating || 0));

  return (
    <div className="flex items-center gap-1 text-[#F59E0B]" aria-label={`${rating || 0} out of 5`}>
      {[1, 2, 3, 4, 5].map((star) =>
        star <= roundedRating ? (
          <FaStar key={star} className="h-4 w-4" />
        ) : (
          <FaRegStar key={star} className="h-4 w-4" />
        )
      )}
    </div>
  );
}

function RecentFeedbackCard({ feedbackItems }) {
  return (
    <div className="rounded-2xl border border-[#E7ECF3] bg-white p-5 shadow-[0_8px_20px_rgba(21,34,50,0.04)] lg:col-span-2">
      <h2 className="font-roboto text-[20px] font-bold text-[#162033]">
        Latest customer responses
      </h2>

      {feedbackItems.length < 1 ? (
        <div className="mt-5 rounded-xl border border-dashed border-[#D4DCE8] bg-[#F8FAFC] px-4 py-6 text-[13px] leading-5 text-[#667085]">
          Recent customer ratings and written feedback will appear here after completed orders are reviewed.
        </div>
      ) : (
        <div className="mt-5 grid gap-3">
          {feedbackItems.map((feedback) => (
            <article
              key={`${feedback.orderId}-${feedback.completedDate}`}
              className="rounded-xl border border-[#E7ECF3] bg-[#F8FAFC] px-4 py-4"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    {feedback.rating > 0 && <StarRating rating={feedback.rating} />}
                    <span className="font-roboto text-[14px] font-bold text-[#162033]">
                      {feedback.orderId}
                    </span>
                    <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-[#667085] ring-1 ring-[#E7ECF3]">
                      {feedback.subject}
                    </span>
                  </div>
                  <p className="mt-3 text-[13px] leading-6 text-[#475467]">
                    {feedback.customerFeedback || "Customer left a star rating without written feedback."}
                  </p>
                </div>
                <span className="shrink-0 text-[12px] font-semibold text-[#8894A6]">
                  {formatDate(feedback.completedDate)}
                </span>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

const PublicWriterProfile = () => {
  const { writerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [writer, setWriter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const returnToCourse = typeof location.state?.returnTo === "string" && /^\/courses\/COURSE-[1-9]\d{4,}\/messages$/.test(location.state.returnTo)
    ? location.state.returnTo : null;
  const returnToThread =
    typeof location.state?.returnTo === "string" &&
    location.state.returnTo.startsWith("/thread")
      ? location.state.returnTo
      : null;
  const backRedirect =
    typeof location.state?.backRedirect === "string"
      ? location.state.backRedirect
      : "dashboard";

  useEffect(() => {
    const previousTitle = document.title;
    const robotsMeta =
      document.querySelector('meta[name="robots"]') || document.createElement("meta");
    const previousRobots = robotsMeta.getAttribute("content");

    robotsMeta.setAttribute("name", "robots");
    robotsMeta.setAttribute("content", "noindex,nofollow");
    document.head.appendChild(robotsMeta);
    document.title = `${writerId || "Writer"} | Writer Profile`;

    return () => {
      document.title = previousTitle;
      if (previousRobots) {
        robotsMeta.setAttribute("content", previousRobots);
      } else {
        robotsMeta.remove();
      }
    };
  }, [writerId]);

  useEffect(() => {
    const fetchWriter = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await api.get(`writer/public/${writerId}`, { voidAuth: true });
        setWriter(response.data?.data);
      } catch (err) {
        setError(err?.message || "Writer profile unavailable.");
      } finally {
        setLoading(false);
      }
    };

    fetchWriter();
  }, [writerId]);

  const fullName = `${writer?.title || ""} ${writer?.firstName || ""} ${writer?.lastName || ""}`.trim();
  const initials = getInitials(writer?.firstName, writer?.lastName);
  const ratingSummary = normalizeRatingSummary(writer?.ratingSummary);
  const performanceSnapshot = normalizePerformanceSnapshot(writer?.performanceSnapshot);
  const recentFeedback = normalizeRecentFeedback(writer?.recentFeedback || []);
  const isVerifiedWriter = (writer?.education || []).some((item) => item.verified);
  const companyTenure = formatCompanyTenure(writer?.createdAt);
  const writerLevel = writer?.experience?.[0];
  const countryFlag = getCountryFlag(writer?.country);
  const successRate =
    performanceSnapshot.successRate === null
      ? "Not available"
      : `${Math.round(performanceSnapshot.successRate)}%`;

  const visibleEducation = useMemo(() => writer?.education || [], [writer?.education]);

  return (
    <main className="min-h-screen bg-[#F8FAFC] px-4 py-8 font-inter sm:px-6 lg:px-8">
      {(returnToThread || returnToCourse) && (
        <div className="mx-auto mb-4 max-w-[1040px]">
          <button
            type="button"
            onClick={() =>
              navigate(returnToCourse || returnToThread, {
                replace: true,
                state: { backRedirect },
              })
            }
            className="inline-flex h-10 items-center justify-center rounded-lg border border-[#DDE6F1] bg-white px-4 text-[13px] font-semibold text-primary shadow-[0_6px_14px_rgba(15,23,42,0.05)] transition hover:bg-[#F8FAFC]"
          >
            {returnToCourse ? "Back to course" : "Back to thread"}
          </button>
        </div>
      )}
      <div className="mx-auto max-w-[1040px] overflow-hidden rounded-2xl border border-[#DDE6F1] bg-white shadow-[0_16px_40px_rgba(15,23,42,0.06)]">
        {loading ? (
          <div className="p-8">
            <div className="h-8 w-48 animate-pulse rounded bg-[#E7ECF3]" />
            <div className="mt-6 h-40 animate-pulse rounded-2xl bg-[#EEF4FA]" />
          </div>
        ) : error ? (
          <div className="p-8">
            <h1 className="font-roboto text-[24px] font-bold text-primary">Writer profile unavailable</h1>
            <p className="mt-3 text-[14px] leading-6 text-gray">{error}</p>
          </div>
        ) : (
          <>
            <section className="bg-gradient-to-r from-[#0F2E4A] via-[#164A72] to-[#176B68] px-5 py-7 sm:px-8">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <div className="relative h-[112px] w-[112px] shrink-0 overflow-hidden rounded-2xl border border-white/30 bg-white/10 shadow-lg">
                    {writer?.profilePicture ? (
                      <img src={writer.profilePicture} alt="Writer profile" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-[#EAF3F7] font-roboto text-[30px] font-bold text-[#164A72]">
                        {initials}
                      </div>
                    )}
                    {isVerifiedWriter && (
                      <span className="absolute bottom-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-[#DBEAFE] text-[#2563EB] ring-2 ring-white">
                        <FaCheckCircle className="h-4 w-4" />
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h1 className="font-roboto text-[30px] font-bold leading-tight text-white">
                        {fallbackValue(fullName)}
                      </h1>
                      {isVerifiedWriter && (
                        <span className="rounded-full bg-[#E0F2FE] px-3 py-1 text-[12px] font-bold text-[#0369A1] ring-1 ring-[#BAE6FD]">
                          Verified Writer
                        </span>
                      )}
                      {countryFlag && (
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/12 px-3 py-1 text-[12px] font-bold text-white ring-1 ring-white/20">
                          <img src={countryFlag.url} alt={countryFlag.alt} className="h-4 w-6 rounded-sm object-cover" />
                          {countryFlag.label}
                        </span>
                      )}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <span className="rounded-full bg-white px-3 py-1 text-[12px] font-bold text-[#164A72]">
                        Writer ID: {fallbackValue(writer?.id)}
                      </span>
                      <span className="rounded-full bg-white/12 px-3 py-1 text-[12px] font-bold text-white ring-1 ring-white/20">
                          {companyTenure} with CoursePivot
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="grid gap-5 p-5 sm:p-7 lg:grid-cols-[1.1fr_0.9fr]">
              <div className="rounded-2xl border border-[#E7ECF3] bg-white p-5 shadow-[0_8px_20px_rgba(21,34,50,0.04)]">
                <p className="text-[12px] font-semibold uppercase tracking-[0.1em] text-aqua-teal">Bio</p>
                <h2 className="mt-1 font-roboto text-[20px] font-bold text-[#162033]">About me</h2>
                <p className="mt-4 text-[14px] leading-6 text-[#475467]">
                  {writer?.bio || "This writer profile is being updated."}
                </p>
                {writerLevel && (
                  <span className="mt-4 inline-flex rounded-full bg-[#E0F2FE] px-3 py-1 text-[12px] font-bold text-[#0369A1] ring-1 ring-[#BAE6FD]">
                    {writerLevel}
                  </span>
                )}
              </div>

              <div className="rounded-2xl border border-[#E7ECF3] bg-white p-5 shadow-[0_8px_20px_rgba(21,34,50,0.04)]">
                <p className="text-[12px] font-semibold uppercase tracking-[0.1em] text-aqua-teal">Rating</p>
                <div className="mt-3 flex items-end gap-2">
                  <span className="font-roboto text-[38px] font-bold leading-none text-[#162033]">
                    {ratingSummary.overallRating.toFixed(1)}
                  </span>
                  <span className="mb-1 text-[13px] font-semibold text-[#667085]">out of 5</span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <StarRating rating={ratingSummary.overallRating} />
                  <span className="text-[13px] font-medium text-[#667085]">
                    {ratingSummary.ratedOrdersCount} rated orders
                  </span>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl bg-[#F8FAFC] px-4 py-3 text-[13px] font-semibold text-[#475467]">
                    {ratingSummary.completedOrdersCount} completed orders
                  </div>
                  <div className="rounded-xl bg-[#F0F9FF] px-4 py-3 text-[13px] font-semibold text-[#0369A1]">
                    {successRate} success rate
                  </div>
                </div>
                <p className="mt-3 text-[12px] leading-5 text-[#667085]">
                  Success rate shows how often finished order outcomes were successful.
                </p>
              </div>

              <div className="rounded-2xl border border-[#E7ECF3] bg-white p-5 shadow-[0_8px_20px_rgba(21,34,50,0.04)] lg:col-span-2">
                <p className="text-[12px] font-semibold uppercase tracking-[0.1em] text-aqua-teal">Academic Credentials</p>
                <div className="mt-5 grid gap-3">
                  {visibleEducation.length ? (
                    visibleEducation.map((item, index) => (
                      <article key={`${item.degree}-${item.school}-${index}`} className="rounded-xl border border-[#E7ECF3] bg-[#F8FAFC] px-4 py-4">
                        <div className="font-roboto text-[15px] font-bold text-[#162033]">
                          {fallbackValue(item.degree)}
                          {item.verified && (
                            <span className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#DBEAFE] align-middle text-[#2563EB]">
                              <FaCheckCircle className="h-3.5 w-3.5" />
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-[13px] font-semibold text-[#475467]">{fallbackValue(item.school)}</div>
                        {(item.achievement || item.year) && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {item.achievement && <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-[#667085] ring-1 ring-[#E7ECF3]">{item.achievement}</span>}
                            {item.year && <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-[#667085] ring-1 ring-[#E7ECF3]">{item.year}</span>}
                          </div>
                        )}
                      </article>
                    ))
                  ) : (
                    <div className="rounded-xl border border-dashed border-[#D4DCE8] bg-[#F8FAFC] px-4 py-6 text-[13px] text-[#667085]">
                      Academic credentials are being updated.
                    </div>
                  )}
                </div>
              </div>

              <RecentFeedbackCard feedbackItems={recentFeedback} />
            </section>
          </>
        )}
      </div>
    </main>
  );
};

export default PublicWriterProfile;
